import { useCallback, useMemo } from "react";
import { debug as debugFn } from "debug";
import { errAsync, ResultAsync } from "neverthrow";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { match } from "ts-pattern";

import type {
  IncidentWorkItemWithContext,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  hasPlaceOfSafety,
  IncidentFormTypeSchema,
  IncidentWorkItemIdSchema,
  workflows,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { useUser } from "../../Context/UserContext.js";
import type { McaAccompanimentFormProps } from "../../forms/McaAccompaniment/McaAccompanimentForm.js";
import {
  McaAccompanimentForm,
  McaAccompanimentFormSkeleton,
} from "../../forms/McaAccompaniment/McaAccompanimentForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

const debug = debugFn("vision-frontend:McaAccompanimentPage");

export type McaAccompanimentPageProps = Record<string, unknown>;

// This component is responsible for managing the API calls and passing
// the data to the inner component.
export function McaAccompanimentPage(_props: McaAccompanimentPageProps) {
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [
    { data: incidentWorkItem, error: incidentWorkItemError },
    refetchWorkItem,
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  const { user } = useUser();

  if (incidentWorkItemError || user === undefined) {
    return <ErrorComponent />;
  }

  return (
    <McaAccompanimentPageInner
      incidentWorkItem={incidentWorkItem ?? undefined}
      refetchWorkItem={refetchWorkItem}
    />
  );
}

export type McaAccompanimentPageInnerProps = {
  incidentWorkItem?: IncidentWorkItemWithContext;
  refetchWorkItem: ReturnType<typeof apiHooks.incidentWorkItem.get>[1];
};

export function McaAccompanimentPageInner({
  incidentWorkItem,
  refetchWorkItem,
}: McaAccompanimentPageInnerProps) {
  if (incidentWorkItem === undefined) {
    return <McaAccompanimentPageInnerLoading />;
  }

  const hasMcaForm = incidentWorkItem.forms.some(
    (form) => form.formType === IncidentFormTypeSchema.enum.mentalCapacityAct,
  );
  if (!hasMcaForm) {
    return (
      <Navigate
        replace
        to={createRoute.incidentProgressPage(
          incidentWorkItem.incidentWorkItem.id,
        )}
      />
    );
  }

  return (
    <McaAccompanimentPageInnerLoaded
      incidentWorkItem={incidentWorkItem}
      refetchWorkItem={refetchWorkItem}
    />
  );
}

function McaAccompanimentPageInnerLoading() {
  return <McaAccompanimentFormSkeleton />;
}

export function McaAccompanimentPageInnerLoaded({
  incidentWorkItem,
  refetchWorkItem,
}: Required<McaAccompanimentPageInnerProps>) {
  const navigate = useNavigate();
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();

  const incidentWorkItemId = incidentWorkItem.incidentWorkItem.id;
  const workflowVersion = incidentWorkItem.incidentWorkItem
    .workflowVersion as WorkflowVersion;

  const mcaForm = useMemo(
    () =>
      incidentWorkItem.forms.find(
        (form) =>
          form.formType === IncidentFormTypeSchema.enum.mentalCapacityAct,
      ),
    [incidentWorkItem.forms],
  );
  if (!mcaForm) {
    throw new Error("Mental Capacity Act form not found");
  }

  const mcaFormId = mcaForm.id;

  // Get the form specification (including pre-computed defaultValues)
  const formSpecification = useMemo(() => {
    const existingData =
      mcaForm.sections.find(
        (section) => section.id === FormSectionSchema.enum.mcaAccompaniment,
      )?.data ?? {};
    return workflows[workflowVersion]?.[
      "mentalCapacityAct"
    ].formDefinition.sections
      .find((section) => section.id === FormSectionSchema.enum.mcaAccompaniment)
      ?.buildFormSpecification({ incidentWorkItem, existingData });
  }, [workflowVersion, incidentWorkItem, mcaForm.sections]);

  if (!formSpecification) {
    throw new Error("Form specification not found");
  }

  const { formSchema: schema, defaultValues: initialValues } =
    formSpecification;

  const existingValuesWereValid = useMemo(() => {
    const existingData =
      mcaForm.sections.find(
        (section) => section.id === FormSectionSchema.enum.mcaAccompaniment,
      )?.data ?? {};
    return schema.safeParse(existingData).success;
  }, [schema, mcaForm.sections]);

  const enableAutoSave = useMemo(
    () =>
      workflows[workflowVersion]?.[
        "mentalCapacityAct"
      ].formDefinition.sections.find(
        (section) => section.id === FormSectionSchema.enum.mcaAccompaniment,
      )?.autoSave ?? false,
    [workflowVersion],
  );

  const onAutoSave: Exclude<
    McaAccompanimentFormProps<typeof workflowVersion>["onAutoSave"],
    undefined
  > = useCallback(
    (values) => {
      if (existingValuesWereValid) {
        // Because we started off with a valid and complete form, we should not regress
        // to a partial form.
        const isValid = schema.safeParse(values).success;
        if (!isValid) {
          return errAsync("Blocked auto-save: form is not valid");
        }
      }
      return ResultAsync.fromPromise(
        api.incidentWorkItem.updateData(
          incidentWorkItemId,
          mcaFormId,
          FormSectionSchema.enum.mcaAccompaniment,
          values,
          existingValuesWereValid,
        ),
        (error) =>
          error instanceof Error ? error.message : JSON.stringify(error),
      )
        .orTee(debug)
        .map(() => true);
    },
    [incidentWorkItemId, existingValuesWereValid, schema, mcaFormId],
  );

  const onSubmit: McaAccompanimentFormProps<
    typeof workflowVersion
  >["onSubmit"] = useCallback(
    async (values: Record<string, unknown>) => {
      try {
        const response = await api.incidentWorkItem.updateData(
          incidentWorkItemId,
          mcaFormId,
          FormSectionSchema.enum.mcaAccompaniment,
          values,
          true,
        );
        if (response.status === 204) {
          await refetchWorkItem();
          // Extract the accompanyingToEmergencyDepartment value from the submitted form
          const accompanyingValue = (
            values.accompanyingToEmergencyDepartment as
              { value: "yes" | "no" } | undefined
          )?.value;

          const destination = match(accompanyingValue)
            .with("yes", () =>
              hasPlaceOfSafety({ incidentWorkItem })
                ? createRoute.incidentReviewPlacesOfSafetyPage(
                    incidentWorkItemId,
                  )
                : createRoute.incidentRecordRetrospectivePlaceOfSafetyPage(
                    incidentWorkItemId,
                  ),
            )
            .with("no", () =>
              createRoute.incidentFinishFormPage(incidentWorkItemId),
            )
            .with(undefined, () =>
              createRoute.incidentProgressPage(incidentWorkItemId),
            )
            .exhaustive();

          navigate(destination);
          return;
        } else {
          throw new Error(JSON.stringify(response.data.message));
        }
      } catch (error) {
        debug("Failed to update MCA Accompaniment section", error);
        toastNotifications.show(
          t("incidentWorkItem.notificationFailedToSave"),
          {
            severity: "error",
          },
        );
      }
    },
    [
      incidentWorkItem,
      incidentWorkItemId,
      navigate,
      refetchWorkItem,
      mcaFormId,
      t,
      toastNotifications,
    ],
  );

  const onCancel: McaAccompanimentFormProps<
    typeof workflowVersion
  >["onCancel"] = useCallback(() => {
    navigate(createRoute.incidentProgressPage(incidentWorkItemId));
  }, [incidentWorkItemId, navigate]);

  return (
    <McaAccompanimentForm
      onAutoSave={enableAutoSave ? onAutoSave : undefined}
      onSubmit={onSubmit}
      onCancel={onCancel}
      initialValues={initialValues}
      incidentWorkItem={incidentWorkItem}
      workflowVersion={workflowVersion}
    />
  );
}
